import * as React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import BestForLabel from "@/components/BestForLabel";
import {
  trackCheckoutEvent,
  generateCheckoutSessionId,
  type CheckoutEventType,
} from "@/lib/checkout-analytics";

// ── Types ───────────────────────────────────────────────────
interface SmartCheckoutRequest {
  userId: string | null;
  vertical: "travel" | "education" | "healthcare";
  amount: number;
  currency: string;
  merchantId: string;
  merchantProviders: string[];
  isReturningUser: boolean;
  userPointsTier: "bronze" | "silver" | "gold" | null;
}

interface RecommendedPlan {
  instalments: number;
  monthlyAmount: number;
  currency: string;
  interestRate: number;
}

interface AltProvider {
  provider: string;
  instalments: number;
  monthlyAmount: number;
  tag: string;
}

interface SmartCheckoutResponse {
  recommendedProvider: string;
  recommendedPlan: RecommendedPlan;
  approvalProbability: "high" | "medium" | "low";
  suggestedDeposit: number;
  reasoning: string;
  upsellOffer: string | null;
  alternativeProviders: AltProvider[];
}

// ── Props ───────────────────────────────────────────────────
interface SmartCheckoutProps {
  vertical: "travel" | "education" | "healthcare";
  amount: number;
  currency?: string;
  merchantId: string;
  merchantProviders?: string[];
  onSelect: (provider: string, plan: RecommendedPlan) => void;
  onBack?: () => void;
}

// ── Approval badge ──────────────────────────────────────────
function ApprovalBadge({ level }: { level: "high" | "medium" | "low" }) {
  const config = {
    high: { dot: "bg-green-500", text: "High approval likelihood", color: "text-green-700" },
    medium: { dot: "bg-amber-500", text: "Good approval likelihood", color: "text-amber-700" },
    low: { dot: "bg-muted-foreground", text: "Approval subject to assessment", color: "text-muted-foreground" },
  };
  const c = config[level];
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${c.dot}`} />
      <span className={`text-xs font-body ${c.color}`}>{c.text}</span>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────
export default function SmartCheckoutRecommendation({
  vertical,
  amount,
  currency = "AED",
  merchantId,
  merchantProviders = ["tabby", "tamara", "cashew"],
  onSelect,
  onBack,
}: SmartCheckoutProps) {
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [result, setResult] = React.useState<SmartCheckoutResponse | null>(null);
  const [showAlts, setShowAlts] = React.useState(false);
  const [upsellAccepted, setUpsellAccepted] = React.useState(false);
  const [depositPct, setDepositPct] = React.useState<number>(0);
  const timedOut = React.useRef(false);

  // ── Tracking refs ─────────────────────────────────────────
  const sessionId = React.useRef(generateCheckoutSessionId());
  const openedAt = React.useRef(Date.now());
  const recommendedProvider = React.useRef<string | null>(null);

  const baseTrackData = React.useMemo(
    () => ({
      sessionId: sessionId.current,
      userId: user?.id ?? null,
      vertical,
      merchantId,
      amount,
      currency,
    }),
    [user?.id, vertical, merchantId, amount, currency]
  );

  const track = React.useCallback(
    (event: CheckoutEventType, meta: Record<string, unknown> = {}) => {
      trackCheckoutEvent(event, { ...baseTrackData, metadata: meta });
    },
    [baseTrackData]
  );

  // ── Track checkout opened + abandoned on unmount ──────────
  React.useEffect(() => {
    track("checkout_opened");
    return () => {
      track("checkout_abandoned", {
        timeSpentMs: Date.now() - openedAt.current,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Track upsell viewed once result loads ─────────────────
  const upsellTracked = React.useRef(false);
  React.useEffect(() => {
    if (result?.upsellOffer && !upsellTracked.current) {
      upsellTracked.current = true;
      track("upsell_viewed", { offer: result.upsellOffer });
    }
  }, [result, track]);

  React.useEffect(() => {
    let cancelled = false;

    const timer = setTimeout(() => {
      if (!cancelled && loading) {
        timedOut.current = true;
        setLoading(false);
      }
    }, 1500);

    async function fetchRecommendation() {
      try {
        const body: SmartCheckoutRequest = {
          userId: user?.id ?? null,
          vertical,
          amount,
          currency,
          merchantId,
          merchantProviders,
          isReturningUser: !!user,
          userPointsTier: null,
        };

        const { data, error } = await supabase.functions.invoke("smart-checkout", { body });

        if (cancelled) return;
        if (error) throw error;

        const res = data as SmartCheckoutResponse;
        setResult(res);
        setDepositPct(res.suggestedDeposit);
        recommendedProvider.current = res.recommendedProvider;

        // Track provider viewed for recommended
        track("provider_viewed", { provider: res.recommendedProvider, isRecommended: true });
      } catch (err) {
        console.error("smart-checkout error, using fallback", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchRecommendation();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived values ──────────────────────────────────────
  const depositAmount = result ? Math.round(amount * (depositPct / 100)) : 0;
  const netAmount = amount - depositAmount;
  const monthlyAmount =
    result
      ? Math.round(
          ((netAmount * (1 + result.recommendedPlan.interestRate / 100)) /
            result.recommendedPlan.instalments) *
            100
        ) / 100
      : 0;

  // ── Handlers with tracking ──────────────────────────────
  const handleProviderSelect = (provider: string, plan: RecommendedPlan) => {
    const timeToDecision = Date.now() - openedAt.current;
    const followedRecommendation = provider === recommendedProvider.current;

    track("provider_selected", {
      provider,
      recommendation: recommendedProvider.current,
      followedRecommendation,
      timeToDecisionMs: timeToDecision,
    });

    if (!followedRecommendation && recommendedProvider.current) {
      track("provider_changed", {
        from: recommendedProvider.current,
        to: provider,
        reason: null,
      });
    }

    onSelect(provider, plan);
  };

  const handleUpsellChange = (accepted: boolean) => {
    setUpsellAccepted(accepted);
    track(accepted ? "upsell_accepted" : "upsell_rejected", {
      offer: result?.upsellOffer,
    });
  };

  const handleDepositChange = (value: number[]) => {
    setDepositPct(value[0]);
    track("deposit_adjusted", { depositPct: value[0] });
  };

  // ── Loading skeleton ────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground font-body animate-pulse">
          Finding your best payment option…
        </p>
        <Skeleton className="h-40 w-full rounded-2xl" />
        <div className="flex gap-3">
          <Skeleton className="h-24 flex-1 rounded-xl" />
          <Skeleton className="h-24 flex-1 rounded-xl" />
        </div>
      </div>
    );
  }

  // ── Static fallback (no result) ─────────────────────────
  if (!result) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 mb-2">
          {onBack && (
            <Button variant="outline" size="sm" onClick={onBack}>
              Back
            </Button>
          )}
          <h3 className="text-xl font-heading font-semibold">Choose Your Payment Plan</h3>
        </div>
        <p className="text-sm text-muted-foreground font-body">
          Total: <span className="font-mono">{amount.toLocaleString()} {currency}</span>
        </p>
        {merchantProviders.map((p) => (
          <Card
            key={p}
            className="p-4 cursor-pointer border hover:border-primary/30 transition-colors"
            onClick={() =>
              handleProviderSelect(p, {
                instalments: 4,
                monthlyAmount: Math.round((amount / 4) * 100) / 100,
                currency,
                interestRate: 0,
              })
            }
          >
            <p className="capitalize font-heading font-semibold">{p}</p>
            <p className="text-sm font-mono text-muted-foreground">
              4 × {currency} {(Math.round((amount / 4) * 100) / 100).toLocaleString()}/month
            </p>
          </Card>
        ))}
      </div>
    );
  }

  // ── Smart recommendation UI ─────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        {onBack && (
          <Button variant="outline" size="sm" onClick={onBack}>
            Back
          </Button>
        )}
        <div>
          <h3 className="text-xl font-heading font-semibold">Your Payment Options</h3>
          <p className="text-sm text-muted-foreground font-body">
            Total: <span className="font-mono">{amount.toLocaleString()} {currency}</span>
          </p>
        </div>
      </div>

      {/* ── Recommended provider card ──────────────────────── */}
      <Card className="relative overflow-hidden border-l-[3px] border-l-primary p-5">
        <span className="absolute right-3 top-3 rounded-full bg-flexaura-gold/15 px-3 py-1 text-[11px] font-body font-medium text-flexaura-gold">
          Recommended for you
        </span>

        <p className="text-lg font-heading font-semibold capitalize mb-1">
          {result.recommendedProvider}
        </p>

        <BestForLabel label={result.alternativeProviders.length > 0 ? (result.alternativeProviders.find(() => true)?.tag ?? "") : ""} />

        <p className="mt-3 text-base font-mono">
          {result.recommendedPlan.instalments} × {currency}{" "}
          {monthlyAmount.toLocaleString()}/month
          {result.recommendedPlan.interestRate === 0
            ? " · 0% interest"
            : ` · ${result.recommendedPlan.interestRate}% APR`}
        </p>

        <div className="mt-2">
          <ApprovalBadge level={result.approvalProbability} />
        </div>

        <p className="mt-3 text-[13px] font-body italic text-muted-foreground">
          {result.reasoning}
        </p>

        <Button
          className="mt-4 w-full md:w-auto"
          onClick={() =>
            handleProviderSelect(result.recommendedProvider, {
              ...result.recommendedPlan,
              monthlyAmount,
            })
          }
        >
          Continue with {result.recommendedProvider}
        </Button>
      </Card>

      {/* ── Upsell offer ───────────────────────────────────── */}
      {result.upsellOffer && (
        <div className="flex items-start gap-3 rounded-xl bg-flexaura-gold/10 border border-flexaura-gold/20 p-4">
          <span className="text-lg">⭐</span>
          <div className="flex-1">
            <p className="text-sm font-body font-medium text-foreground">
              {result.upsellOffer}
            </p>
            <label className="mt-2 flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={upsellAccepted}
                onCheckedChange={(v) => handleUpsellChange(v === true)}
              />
              <span className="text-xs font-body text-muted-foreground">
                Yes, apply this offer
              </span>
            </label>
          </div>
        </div>
      )}

      {/* ── Deposit suggestion ─────────────────────────────── */}
      {result.suggestedDeposit > 0 && (
        <div className="rounded-xl border p-4 space-y-3">
          <p className="text-sm font-body text-foreground">
            A <span className="font-mono font-semibold">{depositPct}%</span> deposit (
            <span className="font-mono">{currency} {depositAmount.toLocaleString()}</span>)
            is suggested for first-time bookings. This improves your approval odds.
          </p>
          <Slider
            value={[depositPct]}
            onValueChange={handleDepositChange}
            min={0}
            max={30}
            step={5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground font-mono">
            <span>0%</span>
            <span>30%</span>
          </div>
          <p className="text-xs text-muted-foreground font-body">
            Monthly payment updates:{" "}
            <span className="font-mono font-medium text-foreground">
              {currency} {monthlyAmount.toLocaleString()}/month
            </span>
          </p>
        </div>
      )}

      {/* ── Alternative providers (collapsed) ──────────────── */}
      {result.alternativeProviders.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => {
              const next = !showAlts;
              setShowAlts(next);
              if (next) {
                result.alternativeProviders.forEach((alt) =>
                  track("provider_viewed", { provider: alt.provider, isRecommended: false })
                );
              }
            }}
            className="flex items-center gap-1.5 text-sm font-body text-muted-foreground hover:text-foreground transition-colors"
          >
            See all options{" "}
            {showAlts ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {showAlts && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {result.alternativeProviders.map((alt) => (
                <Card
                  key={alt.provider}
                  className="p-4 cursor-pointer border hover:border-primary/30 transition-colors"
                  onClick={() =>
                    handleProviderSelect(alt.provider, {
                      instalments: alt.instalments,
                      monthlyAmount: alt.monthlyAmount,
                      currency,
                      interestRate: 0,
                    })
                  }
                >
                  <p className="capitalize font-heading font-semibold text-sm">{alt.provider}</p>
                  <BestForLabel label={alt.tag} />
                  <p className="mt-2 text-sm font-mono text-muted-foreground">
                    {alt.instalments} × {currency} {alt.monthlyAmount.toLocaleString()}/month
                  </p>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <p className="text-[11px] leading-snug text-muted-foreground font-body">
        Flexaura does not issue credit. All financing is provided by licensed partners.
        Approval is subject to provider checks and terms.
      </p>
    </div>
  );
}
